import React, { useEffect, useMemo, useState } from "react";
import API, { getMediaUrl } from "../services/api";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";

function AdminPanel() {
  const [tab, setTab] = useState("dashboard");
  const [deposits, setDeposits] = useState([]);
  const [withdraws, setWithdraws] = useState([]);
  const [utr, setUtr] = useState({});
  const [users, setUsers] = useState([]);

  const user = useMemo(() => JSON.parse(localStorage.getItem("user")), []);

  const chartData = [
    { day: "Mon", amount: 1200 },
    { day: "Tue", amount: 2100 },
    { day: "Wed", amount: 800 },
    { day: "Thu", amount: 1600 },
    { day: "Fri", amount: 2400 },
    { day: "Sat", amount: 1800 },
    { day: "Sun", amount: 2200 }
  ];

  useEffect(() => {
    if (!user || user.role !== "admin") {
      window.location.href = "/admin-login";
    }
  }, [user]);

  const loadUsers = async () => {
    try {
      const res = await API.get("/admin/users");
      setUsers(res.data);
    } catch (err) {
      console.log(err);
    }
  };

  const loadData = async () => {
    try {
      const [depositRes, withdrawRes] = await Promise.all([
        API.get("/admin/deposits"),
        API.get("/admin/withdraw-requests")
      ]);

      setDeposits(depositRes.data);
      setWithdraws(withdrawRes.data);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    loadData();
    loadUsers();
  }, []);

  const totalDeposit = deposits
    .filter((d) => d.status === "approved")
    .reduce((sum, item) => sum + Number(item.amount), 0);

  const totalWithdraw = withdraws
    .filter((w) => w.status === "approved")
    .reduce((sum, item) => sum + Number(item.amount), 0);

  const approveDeposit = async (deposit) => {
    await API.post("/admin/approve-deposit", deposit);
    loadData();
  };

  const rejectDeposit = async (id) => {
    await API.post("/admin/reject-deposit", { id });
    loadData();
  };

  const approveWithdraw = async (withdraw) => {
    if (!utr[withdraw.id]) return alert("Enter UTR");

    await API.post("/admin/approve-withdraw", {
      request_id: withdraw.id,
      utr: utr[withdraw.id]
    });

    loadData();
  };

  const rejectWithdraw = async (id) => {
    await API.post("/admin/reject-withdraw", { id });
    loadData();
  };

  const badge = (status) => {
    const colors = {
      pending: "#f59e0b",
      approved: "#10b981",
      rejected: "#ef4444"
    };

    return (
      <span
        style={{
          background: colors[status] || "#64748b",
          color: "#fff",
          padding: "4px 10px",
          borderRadius: "8px",
          fontSize: "12px"
        }}
      >
        {status}
      </span>
    );
  };

  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <div style={sidebar}>
        <h2 style={{ marginBottom: "20px" }}>MATKA KINGS</h2>
        <div onClick={() => setTab("dashboard")} style={menu}>Dashboard</div>
        <div onClick={() => setTab("users")} style={menu}>Users</div>
        <div onClick={() => setTab("deposit")} style={menu}>Deposits</div>
        <div onClick={() => setTab("withdraw")} style={menu}>Withdraw</div>
      </div>

      <div style={main}>
        {tab === "dashboard" && (
          <>
            <h2>Dashboard</h2>

            <div style={cardWrap}>
              <div style={cardGreen}>
                <p>Total Deposit</p>
                <h2>Rs {totalDeposit}</h2>
              </div>

              <div style={cardBlue}>
                <p>Total Withdraw</p>
                <h2>Rs {totalWithdraw}</h2>
              </div>

              <div style={cardPurple}>
                <p>Total Requests</p>
                <h2>{deposits.length + withdraws.length}</h2>
              </div>
            </div>

            <div style={chartBox}>
              <h3>Revenue Trend</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="amount" stroke="#3b82f6" strokeWidth={3} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}

        {tab === "users" && (
          <>
            <h2>Users</h2>
            <table style={table}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Phone</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {users.map((item) => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <td>{item.phone}</td>
                    <td>Rs {item.balance || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {tab === "deposit" && (
          <>
            <h2>Deposits</h2>
            <table style={table}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Amount</th>
                  <th>Screenshot</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {deposits.map((deposit) => (
                  <tr key={deposit.id}>
                    <td>{deposit.id}</td>
                    <td>Rs {deposit.amount}</td>
                    <td>
                      {deposit.screenshot ? (
                        <a
                          href={getMediaUrl(`/uploads/${deposit.screenshot}`)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View
                        </a>
                      ) : (
                        "No file"
                      )}
                    </td>
                    <td>{badge(deposit.status)}</td>
                    <td>
                      {deposit.status === "pending" && (
                        <>
                          <button style={btnGreen} onClick={() => approveDeposit(deposit)}>Approve</button>
                          <button style={btnRed} onClick={() => rejectDeposit(deposit.id)}>Reject</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {tab === "withdraw" && (
          <>
            <h2>Withdraw</h2>
            <table style={table}>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>UTR</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {withdraws.map((withdraw) => (
                  <tr key={withdraw.id}>
                    <td>{withdraw.id}</td>
                    <td>Rs {withdraw.amount}</td>
                    <td>{badge(withdraw.status)}</td>
                    <td>
                      {withdraw.status === "approved" ? (
                        withdraw.utr
                      ) : (
                        <input
                          placeholder="UTR"
                          style={input}
                          onChange={(event) =>
                            setUtr({ ...utr, [withdraw.id]: event.target.value })
                          }
                        />
                      )}
                    </td>
                    <td>
                      {withdraw.status === "pending" && (
                        <>
                          <button style={btnGreen} onClick={() => approveWithdraw(withdraw)}>Approve</button>
                          <button style={btnRed} onClick={() => rejectWithdraw(withdraw.id)}>Reject</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}

const sidebar = {
  width: "240px",
  background: "#0f172a",
  color: "#fff",
  padding: "20px"
};

const menu = {
  padding: "12px",
  marginBottom: "10px",
  borderRadius: "8px",
  cursor: "pointer",
  background: "#1e293b"
};

const main = {
  flex: 1,
  padding: "20px",
  background: "#f1f5f9",
  overflow: "auto"
};

const cardWrap = {
  display: "flex",
  gap: "20px",
  marginTop: "20px"
};

const cardGreen = {
  flex: 1,
  background: "linear-gradient(135deg, #16a34a, #22c55e)",
  color: "#fff",
  padding: "20px",
  borderRadius: "12px"
};

const cardBlue = {
  flex: 1,
  background: "linear-gradient(135deg, #2563eb, #3b82f6)",
  color: "#fff",
  padding: "20px",
  borderRadius: "12px"
};

const cardPurple = {
  flex: 1,
  background: "linear-gradient(135deg, #7c3aed, #a855f7)",
  color: "#fff",
  padding: "20px",
  borderRadius: "12px"
};

const table = {
  width: "100%",
  marginTop: "20px",
  background: "#fff",
  borderRadius: "12px",
  overflow: "hidden"
};

const btnGreen = {
  background: "#10b981",
  color: "#fff",
  border: "none",
  padding: "6px 10px",
  marginRight: "5px",
  borderRadius: "6px"
};

const btnRed = {
  background: "#ef4444",
  color: "#fff",
  border: "none",
  padding: "6px 10px",
  borderRadius: "6px"
};

const input = {
  padding: "6px",
  borderRadius: "6px",
  border: "1px solid #ccc"
};

const chartBox = {
  background: "#fff",
  padding: "20px",
  borderRadius: "12px",
  marginTop: "20px"
};

export default AdminPanel;
